import { useLanguage } from '@vocab/react';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { useAnalytics } from 'src/contexts/AnalyticsContext';
import { useApiClient } from 'src/modules/ApiProvider';
import type {
  DeleteAccountFailure,
  DeleteAccountSuccess,
} from 'src/modules/graphql/types';
import { useTypedSelector } from 'src/reducers';
import { PageUpdateActionType } from 'src/reducers/page/actionCreators';
import { UserActionType } from 'src/reducers/user/actionCreators';
import type { ErrorMessage } from 'src/types';

import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import Form from './Form';

const DeleteAccountForm = ({ toggle }: { toggle: () => void }) => {
  const { trackAccountLink } = useAnalytics();
  const email = useTypedSelector((state) => state.user.emailAddress);
  const { language } = useLanguage();
  const dispatch = useDispatch();
  const api = useApiClient();

  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = useState<ErrorMessage>();

  const [open, setOpen] = React.useState(false);

  const openDialog = () => {
    setOpen(true);
    trackAccountLink({
      eventName: 'delete_account_pressed',
      currentPageSection: 'delete account',
      passwordlessAuthentication: 'full',
    });
  };

  const onDelete = () => {
    setSubmitting(true);

    api
      .deleteAccount()
      .then((result: DeleteAccountSuccess | DeleteAccountFailure) => {
        if (result.__typename === 'DeleteAccountSuccess') {
          dispatch({
            type: PageUpdateActionType.SET_DELETE_ACCOUNT_SUCCESS,
          });

          dispatch({
            type: UserActionType.USER_DELETED,
            payload: {
              language,
            },
          });
          return;
        } else if (result.__typename === 'DeleteAccountFailure') {
          const failure = result as DeleteAccountFailure;

          if (failure.error.__typename === 'AccountValidationError') {
            setError(
              failure.error?.message ?? failure.error?.details[0]?.message,
            );
          } else {
            setError(failure.error.message);
          }
        }
        setSubmitting(false);
        setOpen(false);
      })
      .catch((err: any) => {
        setError(err?.message || 'Oops!');
        setSubmitting(false);
        setOpen(false);
      });
  };

  return (
    <>
      <Form
        email={email}
        openDialog={openDialog}
        toggle={toggle}
        error={error}
        submitting={submitting}
      />
      <ConfirmDeleteDialog
        isOpen={open}
        onClose={() => setOpen(false)}
        submitting={submitting}
        onDelete={onDelete}
      />
    </>
  );
};

export default DeleteAccountForm;
